import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Input, type InputProps } from '../ui/input';
import { Label } from '../ui/label';

export interface PasswordInputProps extends Omit<InputProps, 'type'> {
    label: string;
    error?: string;
    containerClassName?: string;
}

/** Campo de contraseña con label, error y botón de ojo para mostrar u ocultar el texto. */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
    ({ label, error, id, className, containerClassName, ...inputProps }, ref) => {
        const { t } = useTranslation('auth');
        const [isVisible, setIsVisible] = React.useState(false);
        const generatedId = React.useId();
        const inputId = id ?? generatedId;
        const messageId = `${inputId}-msg`;
        const hasError = !!error;
        const Icon = isVisible ? EyeOff : Eye;

        return (
            <div className={cn('space-y-1.5', containerClassName)}>
                <Label htmlFor={inputId} className={cn(hasError && 'text-destructive')}>
                    {label}
                </Label>
                <div className="relative">
                    <Input
                        ref={ref}
                        id={inputId}
                        type={isVisible ? 'text' : 'password'}
                        aria-invalid={hasError}
                        aria-describedby={hasError ? messageId : undefined}
                        className={cn(
                            'pr-10',
                            hasError && 'border-destructive focus-visible:ring-destructive/40',
                            className,
                        )}
                        {...inputProps}
                    />
                    <button
                        type="button"
                        onClick={() => {
                            setIsVisible((visible) => !visible);
                        }}
                        aria-label={t(isVisible ? 'passwordInput.hide' : 'passwordInput.show')}
                        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25"
                    >
                        <Icon className="h-4 w-4" aria-hidden />
                    </button>
                </div>
                {hasError && (
                    <p id={messageId} className="text-xs text-destructive">
                        {error}
                    </p>
                )}
            </div>
        );
    },
);
PasswordInput.displayName = 'PasswordInput';
